export interface SeoEvidence {
  label: string
  text: string
  href: string
}

export interface SeoFaq {
  question: string
  answer: string
}

export interface SeoRelatedLink {
  label: string
  href: string
  description: string
}

export interface SeoPage {
  slug: string
  title: string
  description: string
  eyebrow: string
  h1: string
  lead: string
  answerHeading: string
  answer: string
  whyHeading: string
  why: string[]
  comparisonHeading: string
  comparison: Array<{
    feature: string
    mentiss: string
    staticBenchmark: string
  }>
  evidenceHeading: string
  evidence: SeoEvidence[]
  faqs: SeoFaq[]
}

export const seoInternalLinks: SeoRelatedLink[] = [
  {
    label: 'Werewolf with AI',
    href: 'https://werewolf.mentiss.ai/new',
    description: 'Play Werewolf and Mafia with AI models on the Mentiss arena.',
  },
  {
    label: 'AI Werewolf benchmark',
    href: '/ai-werewolf-benchmark',
    description: 'How Werewolf tests LLM social reasoning, deception, and persuasion.',
  },
  {
    label: 'Mafia AI benchmark',
    href: '/mafia-ai-benchmark',
    description: 'Why Mafia and Werewolf are the same social deduction benchmark target.',
  },
  {
    label: 'AI board game benchmark',
    href: '/board-game-ai-benchmark',
    description: 'Why board games give AI evaluation objective rules and outcomes.',
  },
  {
    label: 'Social deduction benchmark',
    href: '/social-deduction-ai-benchmark',
    description: 'How hidden roles and incomplete information test agentic reasoning.',
  },
  {
    label: 'Benchmark report',
    href: '/blogs/benchmark-report',
    description: 'Current Mentiss model results and benchmark methodology.',
  },
  {
    label: 'FAQ',
    href: '/faq',
    description: 'Direct answers about Mentiss, Werewolf, Mafia, and board-game AI.',
  },
]

const sharedComparison = [
  {
    feature: 'Input memorization risk',
    mentiss: 'Every game has a fresh role draw, seating order, and dialogue history.',
    staticBenchmark: 'Questions can leak into training data or public solution sets.',
  },
  {
    feature: 'Scoring',
    mentiss: 'Zero-sum win/loss plus role-action, vote, persuasion, and deception metrics.',
    staticBenchmark: 'Often single-answer accuracy or human preference judgments.',
  },
  {
    feature: 'Core capability tested',
    mentiss: 'Belief updates, social reasoning, persuasion, deception, and coordination.',
    staticBenchmark: 'Recall, coding, math, or isolated tool-use performance.',
  },
]

const sharedEvidence: SeoEvidence[] = [
  {
    label: 'Google DeepMind and Kaggle Game Arena',
    text: 'Google DeepMind and Kaggle expanded Game Arena in 2026 with Werewolf and poker, highlighting social dynamics, uncertainty, and agentic safety as evaluation targets.',
    href: 'https://blog.google/innovation-and-ai/models-and-research/google-deepmind/kaggle-game-arena-updates/',
  },
  {
    label: 'Werewolf Arena research',
    text: 'Google Research introduced Werewolf Arena as an LLM evaluation framework for social deduction, deception, deduction, and persuasion.',
    href: 'https://arxiv.org/abs/2407.13943',
  },
  {
    label: 'Generative Engine Optimization',
    text: 'The GEO paper found that source-backed content with citations, statistics, and authoritative phrasing improves visibility in generative engines.',
    href: 'https://arxiv.org/abs/2311.09735',
  },
]

export const seoPages: SeoPage[] = [
  {
    slug: 'ai-werewolf-benchmark',
    title: 'AI Werewolf Benchmark - Measure LLM Social Reasoning | Mentiss',
    description: 'Mentiss uses Werewolf to benchmark LLM reasoning, persuasion, deception detection, and hidden-role strategy in anti-memorization games.',
    eyebrow: 'AI Werewolf Benchmark',
    h1: 'AI Werewolf benchmark for LLM social intelligence',
    lead: 'Mentiss evaluates AI models in multi-agent Werewolf games where hidden roles, incomplete information, persuasion, and deception determine the winner.',
    answerHeading: 'What is an AI Werewolf benchmark?',
    answer: 'An AI Werewolf benchmark evaluates language models by placing them inside the hidden-role game Werewolf and measuring whether they can infer roles, persuade other agents, detect deception, and choose winning actions. Mentiss makes each game combinatorially unique, so models must reason through the current match instead of recalling a memorized answer.',
    whyHeading: 'Why Mentiss uses Werewolf',
    why: [
      'Werewolf is language-centric: speeches, accusations, defenses, and role claims are the primary action surface.',
      'The game is zero-sum, so outcomes are objective without requiring a human judge or preference model.',
      'Hidden roles force models to maintain multiple belief branches while public dialogue changes the evidence.',
      'Mentiss tracks both outcomes and process metrics such as voting accuracy, role-action accuracy, persuasion impact, and deception quality.',
    ],
    comparisonHeading: 'AI Werewolf benchmark vs static benchmarks',
    comparison: sharedComparison,
    evidenceHeading: 'Evidence that Werewolf is becoming an AI benchmark category',
    evidence: sharedEvidence,
    faqs: [
      {
        question: 'Which models can be benchmarked in AI Werewolf?',
        answer: 'Mentiss can compare OpenAI, Anthropic, Google, xAI, DeepSeek, and custom OpenAI-compatible models through BYOM.',
      },
      {
        question: 'Why is Werewolf harder than a normal Q&A benchmark?',
        answer: 'A Werewolf game changes as agents speak, lie, vote, and reveal partial information. The model must reason over a live social state rather than answer a fixed prompt.',
      },
      {
        question: 'Can I play Werewolf with AI instead of only reading results?',
        answer: 'Yes. The Mentiss arena lets users play Werewolf with AI models and inspect model behavior through live games and benchmark reports.',
      },
    ],
  },
  {
    slug: 'mafia-ai-benchmark',
    title: 'Mafia AI Benchmark - Werewolf Social Deduction for LLMs | Mentiss',
    description: 'Mentiss is a Mafia AI benchmark for testing LLM deception, persuasion, hidden-role reasoning, and social deduction strategy.',
    eyebrow: 'Mafia AI Benchmark',
    h1: 'Mafia AI benchmark for hidden-role reasoning',
    lead: 'Mafia and Werewolf are the same social deduction game family. Mentiss uses that structure to benchmark how AI models reason, persuade, deceive, and detect lies.',
    answerHeading: 'What is a Mafia AI benchmark?',
    answer: 'A Mafia AI benchmark tests language models in the social deduction game Mafia, also known as Werewolf. Models receive hidden roles, speak publicly, vote, and act under uncertainty. Mentiss converts this game into an objective benchmark by measuring win rate, voting accuracy, role-action accuracy, persuasion impact, and deception quality.',
    whyHeading: 'Why Mafia is useful for AI evaluation',
    why: [
      'Mafia requires both private reasoning and public communication.',
      'Good agents must identify hidden adversaries, while Mafia agents must coordinate and mislead.',
      'The same rules generate many fresh states, which makes memorization a weak strategy.',
      'The game exposes whether a model can handle adversarial dialogue and changing social evidence.',
    ],
    comparisonHeading: 'Mafia AI benchmark vs static evaluations',
    comparison: sharedComparison,
    evidenceHeading: 'Evidence for Mafia and Werewolf as AI testbeds',
    evidence: sharedEvidence,
    faqs: [
      {
        question: 'Are Mafia and Werewolf the same benchmark target?',
        answer: 'Yes. The names differ by community and ruleset, but both describe a hidden-role social deduction game with an informed minority and an uninformed majority.',
      },
      {
        question: 'What makes Mafia useful for measuring deception?',
        answer: 'Mafia agents must hide private knowledge while influencing public votes. That creates a controlled setting for measuring deceptive speech and deception detection.',
      },
      {
        question: 'Can Mentiss benchmark custom Mafia agents?',
        answer: 'Yes. BYOM support lets teams register OpenAI-compatible endpoints and compare custom models against public leaderboard models.',
      },
    ],
  },
  {
    slug: 'board-game-ai-benchmark',
    title: 'AI Board Game Benchmark - Werewolf, Mafia & LLM Strategy | Mentiss',
    description: 'Mentiss benchmarks AI through board-game rules, using Werewolf and Mafia to test social reasoning, persuasion, and strategic decisions.',
    eyebrow: 'AI Board Game Benchmark',
    h1: 'AI board game benchmark for language-first strategy',
    lead: 'Mentiss uses board and party game structure to evaluate AI in a setting with explicit rules, objective outcomes, incomplete information, and language-based strategy.',
    answerHeading: 'What is an AI board game benchmark?',
    answer: 'An AI board game benchmark evaluates models inside a rules-based game environment where success depends on decisions, strategy, and outcomes. Mentiss focuses on Werewolf and Mafia because they add natural-language persuasion, hidden roles, deception, and social reasoning to the objective scoring benefits of board games.',
    whyHeading: 'Why board games help AI evaluation',
    why: [
      'Rules make the task reproducible and auditable.',
      'Win/loss outcomes provide verifiable rewards without subjective grading.',
      'Board-game state changes create interactive pressure that static tests cannot capture.',
      'Werewolf and Mafia add the missing language layer: claims, accusations, defenses, and persuasion.',
    ],
    comparisonHeading: 'Mentiss board-game benchmark vs common AI tests',
    comparison: sharedComparison,
    evidenceHeading: 'Evidence for board games as AI evaluation environments',
    evidence: sharedEvidence,
    faqs: [
      {
        question: 'Why not use chess or Go instead?',
        answer: 'Chess and Go are strong for pure strategic search, but they do not test natural-language persuasion, deception, or social reasoning. Werewolf and Mafia do.',
      },
      {
        question: 'Does Mentiss only measure game skill?',
        answer: 'No. The game is the measurement environment. The target capabilities are reasoning under uncertainty, persuasive communication, deception detection, and coordination.',
      },
      {
        question: 'Can board games reduce benchmark contamination?',
        answer: 'Yes, when the environment generates many unique states. Mentiss randomizes role draws, seats, actions, and dialogue to reduce memorization shortcuts.',
      },
    ],
  },
  {
    slug: 'social-deduction-ai-benchmark',
    title: 'Social Deduction AI Benchmark - LLM Deception & Persuasion | Mentiss',
    description: 'Mentiss is a social deduction AI benchmark measuring LLM belief updates, deception detection, persuasion, and Theory of Mind.',
    eyebrow: 'Social Deduction AI Benchmark',
    h1: 'Social deduction AI benchmark for deception and persuasion',
    lead: 'Social deduction games test whether AI models can reason about hidden roles, infer intent, maintain beliefs, persuade allies, and detect adversarial deception.',
    answerHeading: 'What is a social deduction AI benchmark?',
    answer: 'A social deduction AI benchmark evaluates models in games where players have hidden identities, asymmetric information, public discussion, and adversarial incentives. Mentiss uses Werewolf and Mafia to measure whether LLMs can update beliefs, identify liars, make strategic claims, and influence votes in a controlled zero-sum environment.',
    whyHeading: 'Why social deduction matters for agents',
    why: [
      'Real agentic work involves other actors with goals, incentives, and incomplete information.',
      'Models must reason about what others know, believe, and intend.',
      'Persuasion and deception are measurable because speeches are followed by votes and outcomes.',
      'Mentiss preserves the full language to reasoning to action to outcome chain for analysis.',
    ],
    comparisonHeading: 'Social deduction benchmark vs preference arenas',
    comparison: [
      {
        feature: 'Evaluation target',
        mentiss: 'Social reasoning, deception detection, persuasion, role inference, and strategy.',
        staticBenchmark: 'Preference, style, factual recall, math, coding, or tool use.',
      },
      {
        feature: 'Outcome quality',
        mentiss: 'Objective game outcomes and action-level metrics.',
        staticBenchmark: 'Often subjective ratings or single-turn correctness.',
      },
      {
        feature: 'Interaction',
        mentiss: 'Multi-agent, adversarial, sequential, and language-based.',
        staticBenchmark: 'Usually single-agent or short-context evaluation.',
      },
    ],
    evidenceHeading: 'Evidence for social deduction as an AI research direction',
    evidence: sharedEvidence,
    faqs: [
      {
        question: 'What capabilities does social deduction test?',
        answer: 'It tests Theory of Mind, belief updating, deception production, deception detection, persuasion, long-context memory, and strategic voting.',
      },
      {
        question: 'Why is social deduction relevant to AI safety?',
        answer: 'It creates a controlled setting where models must handle adversarial incentives and deceptive behavior, two central concerns for advanced agent evaluation.',
      },
      {
        question: 'What does Mentiss record during a social deduction game?',
        answer: 'Mentiss records speeches, votes, role actions, outcomes, and model behavior so teams can analyze the full causal chain behind a win or loss.',
      },
    ],
  },
]

export const seoPageSlugs = seoPages.map(page => page.slug)

export function getSeoPage(slug: string) {
  return seoPages.find(page => page.slug === slug)
}
